import { NativeQ } from '../data/native-q';
import {
    RpcActivateData,
    RpcBase,
    rpcCommandVersion,
    RpcDeactivateResult,
    RpcInboundCommands,
    RpcInboundHandlers,
    RpcInitData,
    RpcInitResult,
    RpcOpenData,
    RpcOpenResult,
    RpcOutboundCommands,
    RpcPingData,
    RpcPingResult,
    RpcShutdownData,
    RpcShutdownResult,
    RpcType
} from './rpc-base';
import { RpcChannel } from './rpc-channel';

/**
 * RpcToModule class.
 * - Shell uses the instance to communicate to the Module (tool).
 */
export class RpcOutbound extends RpcBase {
    /**
     * Initiates a new instance of the RpcToModule class.
     *
     * @param rpcChannel the rpc channel.
     * @param name the public name of the module.
     * @param origin the origin url.
     */
    constructor(rpcChannel: RpcChannel, name: string, origin: string) {
        super(rpcChannel, name, origin, RpcType.Outbound);
        const commands = Object.keys(RpcInboundCommands);
        commands.forEach((command) => {
            this.register(command, this.emptyHandler);
        });
    }

    /**
     * Registers all handlers at once.
     *
     * @param handlers the module handlers.
     */
    public registerAll(handlers: RpcInboundHandlers): void {
        const handlerNames = Object.keys(handlers);
        handlerNames.forEach((handlerName) => {
            const command = RpcBase.handlerToCommandName(handlerName);
            this.register(command, handlers[handlerName]);
        });
    }

    /**
     * The init command.
     *
     * @param data the RpcInitData data.
     * @return Promise<void> the promise object.
     */
    public init(data: RpcInitData): Promise<RpcInitResult> {
        return this.rpcChannel.post<RpcInitData, RpcInitResult>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Init], version: rpcCommandVersion, data: data });
    }

    /**
     * The open command. (30 seconds waiting time)
     *
     * @param data the RpcOpenData object.
     * @return Promise<RpcOpenResult> the promise object of RpcOpenResult.
     */
    public open(data: RpcOpenData): Promise<RpcOpenResult> {
        return this.rpcChannel.post<RpcOpenData, RpcOpenResult>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Open], version: rpcCommandVersion, data: data }, 30 * 1000);
    }

    /**
     * The activate command.
     *
     * @param data the void object.
     * @return Promise<void> the promise object.
     */
    public activate(data: RpcActivateData): Promise<void> {
        return this.rpcChannel.post<RpcActivateData, void>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Activate], version: rpcCommandVersion, data: data });
    }

    /**
     * The deactivate 2 command used with polling deactivation.
     *
     * @param data the void object.
     * @return Promise<RpcDeactivateResult> the promise object.
     */
    public deactivate2(data: any): Promise<RpcDeactivateResult> {
        // ignore if it gets timed out because the frame could be gone already.
        return this.rpcChannel.post<any, RpcDeactivateResult>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Deactivate2], version: rpcCommandVersion, data: data }, -1);
    }

    /**
     * The shutdown command.
     *
     * @param data the RpcShutdownData object.
     * @return Promise<RpcShutdownResult> the promise object.
     */
    public shutdown(data: RpcShutdownData): Promise<RpcShutdownResult> {
        return this.rpcChannel.post<RpcShutdownData, RpcShutdownResult>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Shutdown], version: rpcCommandVersion, data: data });
    }

    /**
     * The ping command.
     *
     * @param data the RpcPingData object.
     * @return Promise<RpcPingResult> the promise object.
     */
    public ping(data: RpcPingData): Promise<RpcPingResult> {
        return this.rpcChannel.retryPost<RpcPingData>(
            this, { command: RpcOutboundCommands[RpcOutboundCommands.Ping], version: rpcCommandVersion, data: data }, 40, 250);
    }

    /**
     * The empty handler to response always resolved.
     *
     * @param data the node context.
     * @return Promise<any> the promise.
     */
    private emptyHandler(): Promise<any> {
        return NativeQ.resolved<any>('emptyHandler');
    }
}
